import { useConversation } from '@elevenlabs/react';
import { EVA_CONFIG } from 'config/eva';
import type { FormEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ProductRecommendation } from 'utils/eva';
import {
  clearConversation,
  loadConversation,
  parseToolResponse,
  saveConversation,
} from 'utils/eva';

import { safeAsync, safeParseZod } from '../lib/zod-helpers';
import { useChatMachine } from '../model/chat.reducer';
import {
  ConversationStartSessionArgsSchema,
  ElevenLabsMessageSchema,
  ElevenLabsTextResponsePartSchema,
} from '../schemas/chat.schemas';

// Invariant: Duplicate provider messages within this window must not create
// additional visible agent responses. This guards against ElevenLabs sending
// both streamed content and a final onMessage payload for the same text.
const STREAM_DUPLICATE_SUPPRESS_MS = 2000;

export function useEvaChat() {
  const inputRef = useRef<HTMLInputElement>(null);
  // Business rule: Restore conversation state from localStorage on mount.
  // Enables conversation continuity across page reloads within TTL window.
  // Assumes: loadConversation returns either a valid, schema-safe payload or an error.
  // Business rule: We hydrate only when persisted data passes validation, otherwise
  // the session starts clean to avoid carrying forward corrupt state.
  const initialConversationResult = useMemo(() => loadConversation(), []);
  const initialConversation = initialConversationResult.isOk()
    ? initialConversationResult.value
    : null;
  // Constraint: Limit restored messages to maxMessages to prevent unbounded state.
  const initialMessages =
    initialConversation?.messages?.slice(-EVA_CONFIG.maxMessages) ?? [];
  // Business rule: Track ElevenLabs conversation ID to correlate user activity
  // with specific conversations. Restored from persistence when available.
  // Invariant: Ref and state are always updated together. The ref provides
  // synchronous access inside async callbacks (e.g. sendMessage) where React
  // state would still reflect the previous render.
  const conversationIdRef = useRef<string | undefined>(
    initialConversation?.conversationId ?? undefined,
  );
  const [conversationId, setConversationId] = useState<string | undefined>(
    initialConversation?.conversationId ?? undefined,
  );
  const updateConversationId = useCallback((id: string | undefined) => {
    conversationIdRef.current = id;
    setConversationId(id);
  }, []);
  const getConversationId = useCallback(() => conversationIdRef.current, []);
  const { state, dispatch, canSend, isLoading } =
    useChatMachine(initialMessages);
  const recommendations = useMemo(() => {
    if (state.messages.length === 0) return null;

    let lastUserIndex = -1;
    for (let i = state.messages.length - 1; i >= 0; i -= 1) {
      if (state.messages[i]?.isUser) {
        lastUserIndex = i;
        break;
      }
    }

    for (let i = state.messages.length - 1; i > lastUserIndex; i -= 1) {
      const message = state.messages[i];
      if (message && !message.isUser && message.recommendations) {
        return message.recommendations;
      }
    }

    return null;
  }, [state.messages]);
  const pendingRecommendationsRef = useRef<ProductRecommendation | null>(null);
  const suppressPostToolMessagesRef = useRef(false);
  // Tracks whether the current user turn already has a non-empty agent reply.
  // Kept as a ref so post-tool suppression can decide synchronously inside
  // ElevenLabs callbacks before React re-renders messagesRef.
  const hasVisibleAgentReplyForTurnRef = useRef(false);
  const activeStreamMessageIdRef = useRef<string | null>(null);
  const activeStreamTextRef = useRef('');
  const lastStreamMessageIdRef = useRef<string | null>(null);
  const lastStreamTextRef = useRef('');
  const lastStreamStopAtRef = useRef(0);
  // Workaround: Tool and stream callbacks capture stale closures. Ref provides
  // latest messages without adding dependencies that would recreate callbacks.
  const messagesRef = useRef(state.messages);
  messagesRef.current = state.messages;
  const isStreamingRef = useRef(state.isStreaming);
  isStreamingRef.current = state.isStreaming;

  // Business rule: After recommend_products, suppress only *follow-up* agent text
  // once the current turn already has a visible reply. If the tool fires before
  // any agent message is rendered (common with ElevenLabs), still allow the first
  // reply through so the UI does not hang on "Eva is thinking...".
  const shouldSuppressPostToolAgentText = useCallback(() => {
    return (
      suppressPostToolMessagesRef.current &&
      !activeStreamMessageIdRef.current &&
      hasVisibleAgentReplyForTurnRef.current
    );
  }, []);

  const markVisibleAgentReply = useCallback((text: string) => {
    if (text.trim().length > 0) {
      hasVisibleAgentReplyForTurnRef.current = true;
    }
  }, []);

  // Business rule: Recommendations arrive via tool callback before agent message
  // completes. Buffer until message exists and is non-empty to attach correctly.
  const flushPendingRecommendations = useCallback(
    (messageId?: string) => {
      const pending = pendingRecommendationsRef.current;
      if (!pending) return;
      if (isStreamingRef.current) return;

      const messages = messagesRef.current;
      const targetMessage = messageId
        ? messages.find((message) => message.id === messageId)
        : (() => {
            for (let i = messages.length - 1; i >= 0; i -= 1) {
              const message = messages[i];
              if (message && !message.isUser) return message;
            }
            return null;
          })();

      if (!targetMessage || targetMessage.isUser) return;
      if (targetMessage.text.trim().length === 0) {
        return;
      }
      if (targetMessage.recommendations) {
        if (messageId) {
          pendingRecommendationsRef.current = null;
        }
        return;
      }

      pendingRecommendationsRef.current = null;
      dispatch({
        type: 'ATTACH_RECOMMENDATIONS',
        id: targetMessage.id,
        recommendations: pending,
      });
    },
    [dispatch],
  );

  const handleInterruption = useCallback(() => {
    pendingRecommendationsRef.current = null;

    const activeStreamId = activeStreamMessageIdRef.current;
    if (activeStreamId) {
      dispatch({ type: 'AGENT_STREAM_COMPLETE', id: activeStreamId });
      lastStreamMessageIdRef.current = activeStreamId;
      lastStreamTextRef.current = activeStreamTextRef.current;
      lastStreamStopAtRef.current = Date.now();
      activeStreamMessageIdRef.current = null;
      activeStreamTextRef.current = '';
    }

    dispatch({ type: 'SET_THINKING', isThinking: false });
    dispatch({ type: 'CONNECTED' });
  }, [dispatch]);

  const handleStreamPart = useCallback(
    (part: unknown) => {
      safeParseZod(ElevenLabsTextResponsePartSchema, part)
        .map((responsePart) => {
          const { type, text } = responsePart;
          let messageId = activeStreamMessageIdRef.current;

          if (!messageId) {
            if (shouldSuppressPostToolAgentText()) return undefined;
            if (type === 'stop' && text.length === 0) {
              return undefined;
            }

            messageId = `a-${Date.now()}`;
            activeStreamMessageIdRef.current = messageId;
            activeStreamTextRef.current = '';
            lastStreamMessageIdRef.current = null;
            lastStreamTextRef.current = '';
            lastStreamStopAtRef.current = 0;
          }

          if (type === 'start') {
            activeStreamTextRef.current = text;
          } else {
            activeStreamTextRef.current += text;
          }

          markVisibleAgentReply(activeStreamTextRef.current);
          dispatch({
            type: 'AGENT_MESSAGE_STREAMED',
            id: messageId,
            text: activeStreamTextRef.current,
          });

          if (type === 'stop') {
            dispatch({ type: 'AGENT_STREAM_COMPLETE', id: messageId });
            lastStreamMessageIdRef.current = messageId;
            lastStreamTextRef.current = activeStreamTextRef.current;
            lastStreamStopAtRef.current = Date.now();
            activeStreamMessageIdRef.current = null;
            activeStreamTextRef.current = '';
            flushPendingRecommendations(messageId);
          }

          return undefined;
        })
        .mapErr(() => undefined);
    },
    [
      dispatch,
      flushPendingRecommendations,
      markVisibleAgentReply,
      shouldSuppressPostToolAgentText,
    ],
  );

  const conversation = useConversation({
    clientTools: {
      recommend_products: (parameters: unknown) => {
        const parsed = parseToolResponse(parameters);
        if (parsed.isOk()) {
          pendingRecommendationsRef.current = parsed.value;
          suppressPostToolMessagesRef.current = true;
          flushPendingRecommendations();
          return 'ok';
        }

        dispatch({
          type: 'ERROR',
          errorMessage: 'Invalid product recommendations received.',
        });
        return 'error';
      },
    },
    onMessage: (message: unknown) => {
      if (shouldSuppressPostToolAgentText()) return;

      safeParseZod(ElevenLabsMessageSchema, message)
        .map((msg) => {
          if (msg.type === 'interruption') {
            handleInterruption();
            return undefined;
          }

          const isAgent = msg.source === 'ai' || msg.type === 'agent';
          const text = msg.message ?? msg.text;

          if (isAgent && typeof text === 'string' && text.length > 0) {
            const now = Date.now();
            const lastStreamId = lastStreamMessageIdRef.current;
            const lastStreamText = lastStreamTextRef.current;
            const lastStreamStopAt = lastStreamStopAtRef.current;
            const isWithinSuppressionWindow =
              lastStreamId && now - lastStreamStopAt <= STREAM_DUPLICATE_SUPPRESS_MS;

            // Business rule: Suppress duplicate messages from onMessage if we recently
            // completed a stream. Check for exact match OR if texts are similar
            // (one contains the other, accounting for streaming variations).
            const textsAreSimilar =
              lastStreamText.length > 0 &&
              (lastStreamText === text ||
                text.includes(lastStreamText) ||
                lastStreamText.includes(text));

            const suppressDuplicate = isWithinSuppressionWindow && textsAreSimilar;

            if (suppressDuplicate) {
              lastStreamMessageIdRef.current = null;
              lastStreamTextRef.current = '';
              lastStreamStopAtRef.current = 0;
              return undefined;
            }

            // Business rule: If we're within the suppression window but texts don't match,
            // still suppress to avoid duplicate rendering. The streamed message is already shown.
            if (isWithinSuppressionWindow && lastStreamText.length > 0) {
              lastStreamMessageIdRef.current = null;
              lastStreamTextRef.current = '';
              lastStreamStopAtRef.current = 0;
              return undefined;
            }

            if (
              lastStreamId &&
              now - lastStreamStopAt > STREAM_DUPLICATE_SUPPRESS_MS
            ) {
              lastStreamMessageIdRef.current = null;
              lastStreamTextRef.current = '';
              lastStreamStopAtRef.current = 0;
            }

            const activeStreamId = activeStreamMessageIdRef.current;
            if (activeStreamId) {
              activeStreamTextRef.current = text;
              markVisibleAgentReply(text);
              dispatch({
                type: 'AGENT_MESSAGE_STREAMED',
                id: activeStreamId,
                text,
              });
              dispatch({ type: 'AGENT_STREAM_COMPLETE', id: activeStreamId });
              lastStreamMessageIdRef.current = activeStreamId;
              lastStreamTextRef.current = text;
              lastStreamStopAtRef.current = Date.now();
              activeStreamMessageIdRef.current = null;
              activeStreamTextRef.current = '';
              flushPendingRecommendations(activeStreamId);
            } else {
              // Fallback: ElevenLabs sometimes delivers the final agent reply via
              // onMessage without streaming parts (or after a suppressed stream).
              // Still render so the client does not hang on thinking forever.
              const newId = `a-${Date.now()}`;
              markVisibleAgentReply(text);
              dispatch({
                type: 'AGENT_MESSAGE_RECEIVED',
                id: newId,
                text,
              });
              flushPendingRecommendations(newId);
            }
          }
          return undefined;
        })
        .mapErr(() => undefined);
    },
    onAgentChatResponsePart: handleStreamPart,
    onInterruption: handleInterruption,
    onError: () => {
      dispatch({
        type: 'ERROR',
        errorMessage: 'Something went wrong. Please try again.',
      });
    },
    onConnect: () => {
      dispatch({ type: 'CONNECTED' });
    },
  });

  // Workaround: useConversation hook may return new instance on render.
  // Store in ref to access latest instance in callbacks without dependency issues.
  const conversationRef = useRef(conversation);
  conversationRef.current = conversation;

  const sendMessage = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed) return false;

      if (activeStreamMessageIdRef.current) {
        handleInterruption();
      }

      pendingRecommendationsRef.current = null;
      suppressPostToolMessagesRef.current = false;
      hasVisibleAgentReplyForTurnRef.current = false;
      dispatch({ type: 'CLEAR_ERROR' });

      dispatch({
        type: 'USER_MESSAGE_SENT',
        id: `u-${Date.now()}`,
        text: trimmed,
      });

      const conv = conversationRef.current;

      if (!EVA_CONFIG.agentId) {
        dispatch({
          type: 'ERROR',
          errorMessage: 'Eva is not configured.',
        });
        return false;
      }

      if (conv.status !== 'connected') {
        dispatch({ type: 'CONNECTING' });
        const sessionArgsResult = safeParseZod(
          ConversationStartSessionArgsSchema,
          {
            agentId: EVA_CONFIG.agentId,
            connectionType: 'websocket',
            textOnly: true,
          },
        );

        if (sessionArgsResult.isErr()) {
          dispatch({
            type: 'CONNECT_FAILED',
            errorMessage: 'Invalid session configuration.',
          });
          return false;
        }

        const connectResult = await safeAsync(() =>
          conv.startSession(sessionArgsResult.value),
        );

        connectResult
          .map((convId) => {
            if (convId) {
              updateConversationId(convId);
            }
            dispatch({ type: 'CONNECTED' });
            return undefined;
          })
          .mapErr(() => {
            dispatch({
              type: 'CONNECT_FAILED',
              errorMessage: 'Failed to connect. Please try again.',
            });
            return undefined;
          });

        if (connectResult.isErr()) {
          return false;
        }
      }

      dispatch({ type: 'SET_THINKING', isThinking: true });
      const sendResult = await safeAsync(() =>
        Promise.resolve(conv.sendUserMessage(trimmed)),
      );

      sendResult.mapErr((error) => {
        dispatch({
          type: 'ERROR',
          errorMessage:
            error instanceof Error
              ? error.message
              : 'Failed to send message. Please try again.',
        });
        return undefined;
      });
      return sendResult.isOk();
    },
    [dispatch, handleInterruption],
  );

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      void sendMessage(state.inputValue);
    },
    [state.inputValue, sendMessage],
  );

  const handleInputChange = useCallback(
    (value: string) => {
      dispatch({ type: 'SET_INPUT', value });
    },
    [dispatch],
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Side effect: Persist conversation state to localStorage after each message change.
  // Business rule: Only save when not streaming to avoid excessive writes during rapid updates.
  // Constraint: Trim messages to maxMessages before saving to respect storage limits.
  useEffect(() => {
    if (state.isStreaming) return;

    if (state.messages.length === 0) {
      clearConversation();
      updateConversationId(undefined);
      return;
    }

    const trimmedMessages = state.messages.slice(-EVA_CONFIG.maxMessages);
    saveConversation({
      messages: trimmedMessages,
      lastUpdated: Date.now(),
      conversationId,
    });
  }, [conversationId, state.isStreaming, state.messages]);

  // Invariant: Recommendations must flush after each agent message completes.
  // Ensures recommendations appear in correct order relative to agent responses.
  useEffect(() => {
    const last = state.messages[state.messages.length - 1];
    if (!last || last.isUser) return;
    if (state.isStreaming) return;
    flushPendingRecommendations(last.id);
  }, [flushPendingRecommendations, state.isStreaming, state.messages]);

  return {
    sendMessage,
    canSend,
    conversationId,
    getConversationId,
    error: state.errorMessage,
    handleInputChange,
    handleSubmit,
    inputRef,
    inputValue: state.inputValue,
    setInputValue: (value: string) => dispatch({ type: 'SET_INPUT', value }),
    isLoading,
    isThinking: state.isThinking,
    isStreaming: state.isStreaming,
    activeAgentMessageId: state.activeAgentMessageId,
    activeStreamEventSeq: state.activeStreamEventSeq,
    messages: state.messages,
    recommendations,
  };
}
